/* A task's work (Duncan 2026-10-22): opening a sub's task on a project's Subcontractors tab shows the work the sub
   does for it, not the schedule's properties ("the schedule changes only happen in our actual construction
   schedule"). First case, a lighting task's fixtures: the lighting team's workbook is uploaded on the task and its
   site picked ("per task"); the lines are then fixed here ("fix in Summit") -- changed, added, removed. They're what
   the sub works on E3 Subs' website (its chat builds that side, from Summit's feed).
   Duncan 2026-10-29 ("open it up to a window with separate tabs with the work being one of them"; his picks): tabs --
   Overview first (the task at a glance: the schedule's %, the sub's, the lines done, its billing, who's on it), the
   Work (a Documents task: its Documents), and Billing: the task's Sub SOV links, what the sub bills E3 for it, linked
   here rather than on the Schedule ("when the project managers [are] setting up the schedule, there's a good chance
   that they will not know the subcontract stuff yet"). Opened at a line, it opens on the Work. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui';
import { ModalShell, ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import {
  getTaskWork, importTaskWork, addTaskWorkLine, updateTaskWorkLine, removeTaskWorkLine, removeTaskWork, downloadTaskWorkFile, getTaskWorkProgress,
  getTaskSubSov,
} from '../../api';
import LineWork, { EMPTY_WORK, linePills, openRequests, whoOf, fmtWhen, greenPill, amberPill, redPill, bluePill } from './LineWork';
import { toBase64 } from '../../lib/photos';
import { readFixtureWorkbook, suggestSite } from '../../lib/fixtureWorkbook';
import { fmtSpan, todayYmd, moneyOfCents } from '../../lib/subcontractors';
import { numberLines } from '../../lib/subSov';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import TaskDocuments from './TaskDocuments';
import TaskSubSovLinks from '../TaskSubSovLinks';

const MAX_BYTES = 3 * 1024 * 1024;
/** One of the window's tabs, as a sub card's: text, the open one bold and underlined in the accent. */
const WinTab = ({ label, count, active, onClick }) => (
  <button type="button" role="tab" aria-selected={active} onClick={onClick}
    style={{ display: 'inline-flex', alignItems: 'center', border: 'none', borderBottom: `2.5px solid ${active ? 'var(--accent)' : 'transparent'}`,
      background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: active ? 650 : 500, whiteSpace: 'nowrap',
      color: active ? 'var(--text-1)' : 'var(--text-3)', padding: '4px 1px 8px' }}>
    {label}
    {count != null && <span className="mono" style={{ marginLeft: 7, fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)' }}>{count}</span>}
  </button>
);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const narrowLabel = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', marginRight: 7 };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const smallBtn = { height: 28, padding: '0 10px', fontSize: 13 };
const input = { width: '100%', height: 32, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' };
const ROW = '52px minmax(0, 1.25fr) minmax(0, 1.6fr) minmax(0, 1.6fr) 62px 36px';
// With the line work (2026-10-23): + Complete and the before / after photos.
const ROW_WORK = '48px minmax(0, 1.15fr) minmax(0, 1.4fr) minmax(0, 1.35fr) 56px 132px 56px 24px';
const SPOT_PILL = { TO_WALK: [bluePill, 'To walk'], PASSED: [greenPill, 'Passed'], FINDINGS: [redPill, 'Findings'] };
const fmtAdded = (ts) => (ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

/** A line's fields, as the form edits them (the sensor as yes / no / not said). */
const FORM_FIELDS = [
  ['building', 'Building'], ['area', 'Room / area'], ['map_id', 'Map ID'], ['ext_qty', 'Existing qty'], ['ext_type', 'Existing fixture'], ['ext_code', 'Existing code'],
  ['pro_qty', 'Proposed qty'], ['pro_type', 'Proposed fixture'], ['pro_tag', 'Proposed tag'], ['ext_comments', 'Existing comments'], ['pro_comments', 'Proposed comments'],
];
const formOf = (l) => ({
  ...Object.fromEntries(FORM_FIELDS.map(([k]) => [k, l && l[k] != null ? String(l[k]) : ''])),
  occ_sensor: l && l.occ_sensor === true ? 'yes' : l && l.occ_sensor === false ? 'no' : '',
});
const payloadOf = (f) => ({ ...f, occ_sensor: f.occ_sensor === 'yes' ? true : f.occ_sensor === 'no' ? false : null });

// The line form's pieces (2026-10-23, the mockup Duncan picked).
const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 };
const textBox = { ...input, height: 'auto', minHeight: 54, padding: '7px 9px', lineHeight: 1.4, resize: 'vertical' };
const qtyBox = { ...input, width: 64, flex: '0 0 64px', textAlign: 'right' };
const groupTitle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 650, color: 'var(--text-1)', marginBottom: 8 };
const groupSide = { borderLeft: '1px solid color-mix(in oklch, var(--accent) 28%, transparent)', paddingLeft: 16 };
const choiceGroup = { display: 'inline-flex', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 7, padding: 2, gap: 2 };
const choiceBtn = { border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--text-3)', fontWeight: 500, fontSize: 13, padding: '4px 10px', borderRadius: 5, fontFamily: 'inherit' };
const choiceOn = { background: 'var(--bg-surface)', color: 'var(--text-1)', boxShadow: 'var(--shadow-xs)' };
const SENSOR = [['', 'Not said'], ['yes', 'Install one'], ['no', 'No']];
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={fieldLabel}>{label}</div>
    {children}
  </div>
);

/** A line's form, in place of its row (Duncan 2026-10-23: editing "needs to be more user intuitive"; the mockup he
    picked): grouped as the row reads -- where it is, what comes out, what goes in -- each quantity beside its
    fixture, the sensor a three-way choice. Enter saves (Ctrl/⌘+Enter in the description), Esc cancels; changes not
    saved are asked about before they're dropped (TaskWorkDialog's okToDrop). */
function LineForm({ initial, title, busy, error, onSave, onCancel, onRemove, onDirty, headerless }) {
  const [f, setF] = useState(() => formOf(initial));
  const [start] = useState(() => formOf(initial));
  const boxRef = useRef(null);
  const firstRef = useRef(null);
  const descRef = useRef(null);
  const dirty = Object.keys(f).some((k) => f[k] !== start[k]);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  useEffect(() => {
    if (headerless) return; // inside a line's panel, which places itself
    if (boxRef.current && boxRef.current.scrollIntoView) boxRef.current.scrollIntoView({ block: 'nearest' });
    if (firstRef.current) firstRef.current.focus();
  }, [headerless]);
  // The description box fits its words (up to about six lines), so a long one reads without scrolling.
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 140)}px`;
  }, [f.ext_code]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = () => { if (!busy) onSave(payloadOf(f)); };
  const onKeyDown = (e) => {
    // Esc leaves this line, not the whole window (ModalShell closes on an Esc that reaches it).
    if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return; }
    if (e.key === 'Enter' && !e.shiftKey && (e.target.tagName === 'INPUT' || e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };
  const text = (k, name, extra) => <input value={f[k]} onChange={set(k)} aria-label={name} style={input} {...extra} />;
  const qtyFixture = (q, t, what) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input value={f[q]} onChange={set(q)} inputMode="numeric" aria-label={`${what} quantity`} style={qtyBox} />
      <span style={{ color: 'var(--text-3)' }}>×</span>
      <input value={f[t]} onChange={set(t)} aria-label={`${what} fixture`} style={input} />
    </div>
  );
  return (
    <div ref={boxRef} className="tw-form" onKeyDown={onKeyDown} style={headerless ? {} : {
      margin: '6px 0 10px', padding: '12px 14px', borderRadius: 12,
      border: '1.5px solid color-mix(in oklch, var(--accent) 45%, transparent)', background: 'var(--accent-soft)',
    }}>
      {!headerless && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px 10px', flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 650, color: 'var(--text-1)' }}>{title}</span>
          {initial.building ? <span style={muted}>{initial.building}</span> : null}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>Enter saves · Esc cancels</span>
        </div>
      )}
      {error && <div style={{ marginBottom: 10 }}><ErrorBanner>{error}</ErrorBanner></div>}
      <div className="tw-form-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.2fr)', gap: 16 }}>
        <div>
          <div style={groupTitle}><Icon name="map-pin" size={13} />Where</div>
          <Field label="Building">{text('building', 'Building')}</Field>
          <Field label="Room / area">{text('area', 'Room or area', { ref: firstRef })}</Field>
          <Field label="Map ID">{text('map_id', 'Map ID', { placeholder: 'None' })}</Field>
        </div>
        <div className="tw-form-group" style={groupSide}>
          <div style={groupTitle}><Icon name="arrow-up" size={13} />Existing · coming out</div>
          <Field label="Quantity × fixture">{qtyFixture('ext_qty', 'ext_type', 'Existing')}</Field>
          <Field label="Code / description">
            <textarea ref={descRef} value={f.ext_code} onChange={set('ext_code')} rows={2} aria-label="Existing code or description" style={textBox} />
          </Field>
          <Field label="Comments">{text('ext_comments', 'Existing comments', { placeholder: 'None' })}</Field>
        </div>
        <div className="tw-form-group" style={groupSide}>
          <div style={groupTitle}><Icon name="arrow-down" size={13} />Proposed · going in</div>
          <Field label="Quantity × fixture">{qtyFixture('pro_qty', 'pro_type', 'Proposed')}</Field>
          <Field label="Tag">{text('pro_tag', 'Proposed tag', { placeholder: 'None' })}</Field>
          <Field label="Comments">{text('pro_comments', 'Proposed comments', { placeholder: 'None' })}</Field>
          <Field label="Occupancy sensor">
            <div role="radiogroup" aria-label="Occupancy sensor" style={choiceGroup}>
              {SENSOR.map(([v, l]) => (
                <button key={v || 'none'} type="button" role="radio" aria-checked={f.occ_sensor === v}
                  onClick={() => setF((x) => ({ ...x, occ_sensor: v }))} style={{ ...choiceBtn, ...(f.occ_sensor === v ? choiceOn : {}) }}>
                  {l}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid color-mix(in oklch, var(--accent) 28%, transparent)' }}>
        {onRemove && (
          <button type="button" onClick={onRemove} style={{ ...linkBtn, color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Icon name="trash" size={13} />Remove line
          </button>
        )}
        <span style={{ flex: 1 }} />
        <SecondaryButton onClick={onCancel} style={smallBtn}>Cancel</SecondaryButton>
        <PrimaryButton busy={busy} onClick={save} style={smallBtn}>{busy ? 'Saving…' : initial.id ? 'Save line' : 'Add line'}</PrimaryButton>
      </div>
    </div>
  );
}

/** One line: where, what's there, what goes in. The whole row opens it (2026-10-23); a long description shows two
    lines here, all of it on hover and in the form. */
function LineRow({ line, onEdit, flash, showWork, work }) {
  const w = work || EMPTY_WORK;
  const pills = showWork ? linePills(w, Number(line.pro_qty) || 0) : []; // E3 Subs' words: Partly done · 2/5, Waiting on material…
  const spot = w.spotCheck && SPOT_PILL[w.spotCheck.status];
  const extNote = [line.ext_code, line.ext_comments].filter(Boolean).join(' · ');
  const proNote = [line.pro_tag && `tag ${line.pro_tag}`, line.pro_comments].filter(Boolean).join(' · ');
  return (
    <div role="button" tabIndex={0} aria-label={`Edit line ${line.line_no ?? ''} ${line.area || ''}`.replace(/\s+/g, ' ').trim()}
      onClick={onEdit} onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onEdit(); } }}
      className={`tw-row tw-click${showWork ? ' tw-work' : ''}${flash ? ' tw-saved' : ''}`}
      style={{ display: 'grid', gridTemplateColumns: showWork ? ROW_WORK : ROW, gap: 10, alignItems: 'start', padding: '7px 6px', margin: '0 -6px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5, cursor: 'pointer' }}>
      <span className="mono tw-no" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{line.line_no ?? '—'}</span>
      <span className="tw-area" style={{ minWidth: 0 }}>
        {line.area || <span style={{ color: 'var(--text-4)' }}>No room set</span>}
        {line.map_id && <span className="mono" style={{ ...muted, display: 'block' }}>map {line.map_id}</span>}
        {pills.length > 0 && (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
            {pills.map(([text, style]) => <span key={text} style={{ ...style, fontSize: 11 }}>{text}</span>)}
          </span>
        )}
      </span>
      <span className="tw-ext" style={{ minWidth: 0 }}>
        <span className="tw-label" style={narrowLabel}>Existing</span>
        <span className="mono" style={{ fontWeight: 600 }}>{num(line.ext_qty)}</span> × {line.ext_type || '—'}
        {extNote && <span className="tw-clamp" title={extNote} style={{ ...muted, display: 'block' }}>{extNote}</span>}
      </span>
      <span className="tw-pro" style={{ minWidth: 0 }}>
        <span className="tw-label" style={narrowLabel}>Proposed</span>
        <span className="mono" style={{ fontWeight: 600 }}>{num(line.pro_qty)}</span> × {line.pro_type || '—'}
        {proNote && <span className="tw-clamp" title={proNote} style={{ ...muted, display: 'block' }}>{proNote}</span>}
      </span>
      <span className={`tw-sen${line.occ_sensor == null ? ' tw-none' : ''}`} style={{ fontSize: 12.5, color: line.occ_sensor ? 'var(--text-1)' : 'var(--text-4)' }}>
        <span className="tw-label" style={narrowLabel}>Occupancy sensor</span>
        {line.occ_sensor === true ? 'Yes' : line.occ_sensor === false ? 'No' : '—'}
      </span>
      {showWork && (
        <span className="tw-done" style={{ minWidth: 0 }}>
          {/* Where the line stands, as a pill (2026-10-23): a click anywhere on the row opens it to finish it. */}
          {w.done ? (
            <span title={`Complete${w.doneBy ? ` · ${whoOf(w.doneBy)}` : ''}${w.doneAt ? `, ${fmtWhen(w.doneAt)}` : ''}`} style={greenPill}>
              <Icon name="check" size={11} />Complete{(w.installers || []).length ? ` · ${w.installers.slice(0, 2).join(' ')}${w.installers.length > 2 ? ` +${w.installers.length - 2}` : ''}` : ''}
            </span>
          ) : w.photos.after.length ? <span style={bluePill}>Ready to complete</span>
            : w.photos.before.length ? <span style={amberPill}>Needs after photo</span>
              : <span style={{ ...greenPill, background: 'var(--bg-surface-2)', color: 'var(--text-3)' }}>{(w.installed || 0) > 0 ? 'In progress' : 'Not started'}</span>}
          {spot && <span style={{ display: 'block', marginTop: 4 }}><span style={{ ...spot[0], fontSize: 11 }}>{spot[1]}</span></span>}
        </span>
      )}
      {showWork && (
        <span className="tw-photos" style={{ display: 'flex', gap: 4 }}>
          {[['B', 'Before', w.photos.before], ['A', 'After', w.photos.after]].map(([letter, label, list]) => (
            <span key={letter} title={`${label}: ${list.length} photo${list.length === 1 ? '' : 's'}`} style={{
              width: 24, height: 24, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700,
              border: list.length ? '1.5px solid color-mix(in oklch, var(--success) 70%, transparent)' : '1px dashed var(--border-strong)',
              background: list.length ? 'var(--success-soft)' : 'transparent', color: list.length ? 'var(--success)' : 'var(--text-4)',
            }}>{letter}</span>
          ))}
        </span>
      )}
      <span className="tw-edit" aria-hidden="true" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 2, color: 'var(--text-4)' }}><Icon name="pencil" size={14} /></span>
    </div>
  );
}

/** A line, open (2026-10-23): its work -- what the sub does, kept on E3 Subs -- and its details, the fixtures fixed in
    Summit, as two tabs. Esc on the Work tab closes it (the details form handles its own Esc). */
function LinePanel({ line, tab, onTab, work, onWorkChange, onDirtyWork, confirm, onExpired, onClose, formProps }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'nearest' }); }, []);
  const onKeyDown = (e) => { if (e.key === 'Escape' && tab === 'work') { e.stopPropagation(); onClose(); } };
  const tabBtn = (k, l) => (
    <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => onTab(k)}
      style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 5,
        background: tab === k ? 'var(--bg-surface)' : 'transparent', color: tab === k ? 'var(--text-1)' : 'var(--text-3)', boxShadow: tab === k ? 'var(--shadow-xs)' : 'none' }}>
      {l}
    </button>
  );
  return (
    <div ref={ref} onKeyDown={onKeyDown} style={{ margin: '6px 0 10px', padding: '12px 14px', borderRadius: 12, border: '1.5px solid color-mix(in oklch, var(--accent) 55%, transparent)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px 10px', flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 650, color: 'var(--text-1)' }}>{line.line_no != null ? `Line ${line.line_no}` : 'Line added in Summit'}</span>
        <span style={muted}>{line.area || line.building || ''}</span>
        {line.pro_qty != null && <span className="mono" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-surface-2)', color: 'var(--text-2)' }}>{line.pro_qty} × {line.pro_type || '—'}</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {tab === 'details' && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Enter saves · Esc cancels</span>}
          <span role="tablist" aria-label="This line" style={{ display: 'inline-flex', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 7, padding: 2, gap: 2 }}>
            {tabBtn('work', 'Work')}{tabBtn('details', 'Edit details')}
          </span>
          <button type="button" onClick={onClose} aria-label="Close this line" title="Close this line"
            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </span>
      </div>
      <div style={{ display: tab === 'work' ? 'block' : 'none' }}>
        <LineWork line={line} work={work} onChange={onWorkChange} onDirty={onDirtyWork} confirm={confirm} onExpired={onExpired} />
      </div>
      <div style={{ display: tab === 'details' ? 'block' : 'none' }}>
        <LineForm headerless {...formProps} />
      </div>
    </div>
  );
}

/* A row shows it opens; a saved one glows a moment. Narrow (a phone, or a small window): each line stacks -- Line #
   and room with the pencil, then what's there, what goes in and the sensor, each labelled -- and the form's three
   groups stack too, instead of anything wider than the window. */
const NARROW_CSS = `
  .tw-label { display: none; }
  .tw-click:hover { background: color-mix(in oklch, var(--bg-hover) 60%, transparent); }
  .tw-click:hover .tw-edit { color: var(--accent-text) !important; }
  .tw-click:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
  .tw-clamp { display: -webkit-box !important; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .tw-saved { animation: tw-saved 1.8s ease-out; }
  @keyframes tw-saved { from { background: var(--accent-soft); } to { background: transparent; } }
  .lw-thumb .lw-remove { opacity: 0; transition: opacity 0.12s; }
  .lw-thumb:hover .lw-remove, .lw-thumb .lw-remove:focus-visible { opacity: 1; }
  @media (hover: none) { .lw-thumb .lw-remove { opacity: 1; } }
  @media (max-width: 860px) {
    .tw-table { min-width: 0 !important; }
    .tw-head { display: none !important; }
    .tw-row { grid-template-columns: 44px minmax(0, 1fr) auto !important; grid-template-areas: "no area edit" ". ext ext" ". pro pro" ". sen sen"; row-gap: 4px !important; }
    .tw-no { grid-area: no; } .tw-area { grid-area: area; font-weight: 600; } .tw-ext { grid-area: ext; } .tw-pro { grid-area: pro; }
    .tw-sen { grid-area: sen; } .tw-sen.tw-none { display: none; } .tw-edit { grid-area: edit; }
    .tw-label { display: inline; }
    .tw-form-cols { grid-template-columns: minmax(0, 1fr) !important; gap: 6px !important; }
    .tw-form-group { border-left: none !important; padding-left: 0 !important; padding-top: 10px; border-top: 1px solid color-mix(in oklch, var(--accent) 28%, transparent); }
    .tw-row.tw-work { grid-template-areas: "no area edit" ". ext ext" ". pro pro" ". sen sen" ". done photos" !important; }
    .tw-done { grid-area: done; } .tw-photos { grid-area: photos; justify-self: end; }
    .lw-boxes { grid-template-columns: minmax(0, 1fr) !important; }
  }
`;

/** The upload: the workbook read here, its sites to pick from (the task's suggested), then imported. */
function ImportStep({ task, hasLines, busy, onImport, onCancel }) {
  const [file, setFile] = useState(null);
  const [read, setRead] = useState(null); // { sheet, lines, sites } | { error }
  const [site, setSite] = useState(null);
  const fileRef = useRef(null);
  const choose = async (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) { setFile(null); setRead({ error: 'The workbook is larger than the 3MB limit.' }); return; }
    setFile(f);
    const buf = await f.arrayBuffer();
    const r = readFixtureWorkbook(buf);
    setRead(r.error ? r : { ...r, buf });
    if (!r.error) setSite(suggestSite(r.sites, task) || (r.sites.length === 1 ? r.sites[0].name : null));
  };
  const chosen = read && !read.error && read.sites.find((s) => s.name === site);
  const suggested = read && !read.error ? suggestSite(read.sites, task) : null;
  const sites = read && !read.error ? [...read.sites].sort((a, b) => (b.name === suggested) - (a.name === suggested)) : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style={{ display: 'none' }} onChange={(e) => choose(e.target.files?.[0] || null)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <SecondaryButton onClick={() => fileRef.current?.click()}><Icon name="upload" size={14} />{file ? 'Choose another' : 'Choose the fixture workbook'}</SecondaryButton>
        <span style={muted}>{file ? `${file.name}${read && !read.error ? ` · “${read.sheet}” sheet · ${read.lines.length} lines, ${read.sites.length} site${read.sites.length === 1 ? '' : 's'}` : ''}` : 'The lighting team’s Excel workbook (.xlsx)'}</span>
      </div>
      {read && read.error && <ErrorBanner>{read.error}</ErrorBanner>}
      {read && !read.error && (
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6, padding: 0 }}>Which site is this task’s?</legend>
          {sites.map((s) => (
            <label key={s.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '5px 0', fontSize: 14, cursor: 'pointer' }}>
              <input type="radio" name="work-site" checked={site === s.name} onChange={() => setSite(s.name)} style={{ marginTop: 3 }} />
              <span>
                {s.name}
                <span style={{ ...muted, display: 'block' }}>
                  {s.lines} line{s.lines === 1 ? '' : 's'} · {s.ext.toLocaleString()} fixture{s.ext === 1 ? '' : 's'} out, {s.pro.toLocaleString()} in · {s.buildings} building{s.buildings === 1 ? '' : 's'}
                  {s.name === suggested ? ' · matches the task’s location' : ''}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {hasLines && chosen && <div style={muted}>This replaces the task’s lines with the workbook’s, fixes made here included. A line whose Line # comes back keeps its place (and what the sub recorded on it); the others, lines added here too, are kept, marked removed.</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <PrimaryButton busy={busy} disabled={!chosen} onClick={() => onImport({ file, read, site: chosen.name })}>
          {busy ? 'Importing…' : chosen ? `Import ${chosen.lines} line${chosen.lines === 1 ? '' : 's'}` : 'Pick the site'}
        </PrimaryButton>
        {onCancel && <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>}
      </div>
    </div>
  );
}

export default function TaskWorkDialog({ taskId, lineId = null, onClose, onOpenSub = null, canSendToCloseout = false }) {
  const navigate = useNavigate();
  const [work, setWork] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false); // the upload step open
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // a line's id, or 'new:<building>'
  const [formError, setFormError] = useState(null);
  const [open, setOpen] = useState(() => new Set()); // buildings opened (the first is, to start)
  const opened = useRef(false); // the first building opened once; after that, as the person leaves them
  const [savedId, setSavedId] = useState(null); // the line just saved, lit a moment
  const dirty = useRef(false); // the open line's details have changes not saved
  const onDirty = useCallback((d) => { dirty.current = d; }, []);
  const dirtyWork = useRef(false); // ... or its work has an issue or findings half written
  const onDirtyWork = useCallback((d) => { dirtyWork.current = d; }, []);
  const [tab, setTab] = useState('work'); // the open line's tab: its work, or its details
  // The line work on E3 Subs (2026-10-23): { on, noSub, error, byLine } -- null while it loads. on: false while E3 Subs
  // isn't connected (no line work shows at all); error when it couldn't be reached (the lines still show).
  const [progress, setProgress] = useState(null);
  const lastRefresh = useRef(0);
  const { confirm, confirmDialogProps } = useConfirm();
  // A Documents task's upload still going (2026-10-24): closing the window asks first.
  const docsUploading = useRef(false);
  const onDocsUploading = useCallback((u) => { docsUploading.current = u; }, []);
  // The window's tab (2026-10-29): Overview first; opened at a line, the Work.
  const [view, setView] = useState(lineId != null ? 'work' : 'overview');
  // The task's Sub SOV links, for the Overview's Billing tile (the Billing tab reads its own).
  const [subSov, setSubSov] = useState(null);
  const loadSubSov = useCallback(() => getTaskSubSov(taskId).then(setSubSov).catch(() => setSubSov(null)), [taskId]);
  useEffect(() => { loadSubSov(); }, [loadSubSov]);

  const load = useCallback(() => getTaskWork(taskId).then((w) => { setWork(w); setError(null); }).catch((e) => setError(e.message || 'Couldn’t load the work')), [taskId]);
  useEffect(() => { load(); }, [load]);
  const loadProgress = useCallback(() => getTaskWorkProgress(taskId)
    .then((p) => setProgress({ on: !!p.on, noSub: !!p.noSub, error: p.error || null, byLine: new Map((p.lines || []).map((l) => [l.lineId, l])),
      technicians: Array.isArray(p.technicians) ? p.technicians : [] }))
    .catch((e) => setProgress({ on: true, noSub: false, error: e.message || 'Couldn’t load the line work', byLine: new Map(), technicians: [] })), [taskId]);
  useEffect(() => { loadProgress(); }, [loadProgress]);
  // A photo's links last about 15 minutes: a broken one fetches fresh ones (once in 30 s at most).
  const onExpired = useCallback(() => {
    if (Date.now() - lastRefresh.current < 30000) return;
    lastRefresh.current = Date.now();
    loadProgress();
  }, [loadProgress]);
  // An action's answer is the line's new state.
  const onWorkChange = useCallback((state) => setProgress((p) => {
    if (!p || !state) return p;
    const byLine = new Map(p.byLine);
    byLine.set(state.lineId, state);
    return { ...p, byLine };
  }), []);

  const groups = useMemo(() => {
    const m = new Map();
    (work ? work.lines : []).forEach((l) => {
      const k = l.building || '(no building)';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(l);
    });
    return [...m.entries()].map(([building, lines]) => ({ building, lines, fixtures: lines.reduce((s, l) => s + (l.pro_qty || 0), 0) }));
  }, [work]);
  useEffect(() => {
    if (!groups.length || opened.current) return;
    opened.current = true;
    // Opened at a line (a sub's Requests and notes, 2026-10-24): its building, and the line open on its work.
    const at = lineId != null ? groups.find((g) => g.lines.some((l) => l.id === lineId)) : null;
    if (at) { setOpen(new Set([at.building])); setTab('work'); setEditing(lineId); return; }
    setOpen(new Set([groups[0].building]));
  }, [groups, lineId]);
  useEffect(() => {
    if (!savedId) return undefined;
    const t = setTimeout(() => setSavedId(null), 1900);
    return () => clearTimeout(t);
  }, [savedId]);

  if (!work) {
    return (
      <ModalShell title="The work" onClose={onClose} width={1100}>
        {error ? <ErrorBanner>{error}</ErrorBanner> : <div style={muted}>Loading…</div>}
      </ModalShell>
    );
  }
  const { task, file, lines } = work;
  const isDocs = task.type === 'Documents';
  const totals = {
    lines: lines.length,
    out: lines.reduce((s, l) => s + (l.ext_qty || 0), 0),
    in: lines.reduce((s, l) => s + (l.pro_qty || 0), 0),
    rooms: new Set(lines.map((l) => `${(l.building || '').toLowerCase()}|${(l.area || '').toLowerCase()}`)).size,
  };
  const site = (file && file.site) || (lines[0] && lines[0].site) || null;
  const showWork = !!(progress && progress.on && !progress.noSub && !progress.error);
  const stateOf = (l) => (showWork && progress.byLine.get(l.id)) || EMPTY_WORK;
  const pr = showWork ? lines.reduce((t, l) => {
    const st = stateOf(l);
    const sc = st.spotCheck;
    return {
      done: t.done + (st.done ? 1 : 0),
      issues: t.issues + openRequests(st).length, // material that's delivered isn't open any more
      walk: t.walk + (sc && sc.status === 'TO_WALK' ? 1 : 0),
      punch: t.punch + (sc && sc.status === 'FINDINGS' ? (sc.findings || []).filter((f) => !f.punch || f.punch.status !== 'VERIFIED').length : 0),
    };
  }, { done: 0, issues: 0, walk: 0, punch: 0 }) : null;

  const doImport = async ({ file: f, read, site: picked }) => {
    setBusy(true);
    setError(null);
    try {
      const chosen = read.lines.filter((l) => (l.site || '(no site)') === picked);
      const next = await importTaskWork(task.id, {
        filename: f.name, contentType: f.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        file: toBase64(read.buf), sheet: read.sheet, site: picked === '(no site)' ? null : picked, lines: chosen,
      });
      setWork(next);
      setImporting(false);
      opened.current = false;
      setOpen(new Set());
      loadProgress();
    } catch (e) {
      setError(e.message || 'Couldn’t import the work');
    } finally {
      setBusy(false);
    }
  };
  const saveLine = async (line, fields) => {
    setBusy(true);
    setFormError(null);
    try {
      if (line && line.id) {
        const saved = await updateTaskWorkLine(line.id, fields);
        setWork((w) => ({ ...w, lines: w.lines.map((l) => (l.id === saved.id ? saved : l)) }));
        setSavedId(saved.id);
      } else {
        const added = await addTaskWorkLine(task.id, { ...fields, site });
        setWork((w) => ({ ...w, lines: [...w.lines, added] }));
        setSavedId(added.id);
      }
      dirty.current = false;
      setEditing(null);
    } catch (e) {
      setFormError(e.message || 'Couldn’t save the line');
    } finally {
      setBusy(false);
    }
  };
  const removeLine = async (line) => {
    const ok = await confirm({ title: 'Remove this line?', message: `${[line.building, line.area].filter(Boolean).join(' · ') || 'The line'} comes off the task’s work. It’s kept, marked removed.`, confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await removeTaskWorkLine(line.id);
      setWork((w) => ({ ...w, lines: w.lines.filter((l) => l.id !== line.id) }));
      dirty.current = false;
      setEditing(null);
    } catch (e) {
      setFormError(e.message || 'Couldn’t remove the line');
    }
  };
  const removeAll = async () => {
    const ok = await confirm({ title: 'Remove the task’s work?', message: `Its ${lines.length} line${lines.length === 1 ? '' : 's'} and the workbook come off the task. They’re kept, marked removed.`, confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await removeTaskWork(task.id);
      leaveLine();
      await load();
      loadProgress();
    } catch (e) {
      setError(e.message || 'Couldn’t remove the work');
    }
  };
  // Changes not saved are asked about before they're dropped: another line opened, Cancel or Esc, Replace, a
  // building closed over the open line, the window closed.
  const okToDrop = async () => {
    if (!editing || !(dirty.current || dirtyWork.current)) return true;
    return confirm({ title: 'Discard your changes?', message: 'This line has changes that aren’t saved.', confirmLabel: 'Discard', danger: true });
  };
  const leaveLine = () => { dirty.current = false; dirtyWork.current = false; setFormError(null); setEditing(null); };
  // A line opens on its work while the line work shows (else straight to its details).
  const startEdit = async (id, which) => {
    if (editing === id) { if (which) setTab(which); return; }
    if (!(await okToDrop())) return;
    dirty.current = false;
    dirtyWork.current = false;
    setFormError(null);
    setTab(which || 'work');
    setEditing(id);
  };
  const cancelEdit = async () => { if (await okToDrop()) leaveLine(); };
  const requestClose = async () => {
    if (docsUploading.current && !(await confirm({
      title: 'Stop the upload?', confirmLabel: 'Close and stop', danger: true,
      message: 'An upload is still going. Closing stops it. Upload the same file to the same folder within 7 days and it carries on where it stopped.',
    }))) return;
    if (await okToDrop()) onClose();
  };
  // To the sub's spot on its card (2026-10-29): its Subcontract card on Documents, or its Billing tab -- closing this
  // window first, as Close does.
  const leaveFor = (where) => async () => {
    if (docsUploading.current && !(await confirm({
      title: 'Stop the upload?', confirmLabel: 'Close and stop', danger: true,
      message: 'An upload is still going. Leaving stops it. Upload the same file to the same folder within 7 days and it carries on where it stopped.',
    }))) return;
    if (await okToDrop()) onOpenSub(task.subcontractor.id, where);
  };
  const toggle = async (b) => {
    const holdsEditing = open.has(b) && editing && (editing === `new:${b}` || (groups.find((g) => g.building === b) || { lines: [] }).lines.some((l) => l.id === editing));
    if (holdsEditing) {
      if (!(await okToDrop())) return;
      leaveLine();
    }
    setOpen((s) => { const n = new Set(s); if (n.has(b)) n.delete(b); else n.add(b); return n; });
  };

  return (
    <ModalShell
      title={`${task.name}${task.code ? ` · ${task.code}` : ''}`}
      subtitle={[task.subcontractor ? task.subcontractor.name : task.responsible_party, task.location, fmtSpan(task.start_date, task.finish_date, todayYmd()), `${task.percent_complete}% done`,
        // Who the sub put on it, on E3 Subs (2026-10-24; names only)
        progress && progress.technicians && progress.technicians.length ? `Technicians: ${progress.technicians.map((x) => x.name).join(', ')}` : null].filter(Boolean).join(' · ')}
      onClose={requestClose} width={isDocs && view === 'work' ? 'min(1700px, 100%)' : 1100} fill={isDocs && view === 'work'} // a Documents task's preview: as big as the screen allows
      footer={<SecondaryButton onClick={requestClose}>Close</SecondaryButton>}
    >
      <style>{NARROW_CSS}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...(isDocs && view === 'work' ? { flex: 1, minHeight: 0 } : {}) }}>
        <div role="tablist" aria-label={`${task.name}: details`} style={{ display: 'flex', alignItems: 'flex-end', gap: 20, borderBottom: '1.5px solid var(--border-default)' }}>
          <WinTab label="Overview" active={view === 'overview'} onClick={() => setView('overview')} />
          <WinTab label={isDocs ? 'Documents' : 'Work'} count={isDocs ? null : lines.length} active={view === 'work'} onClick={() => setView('work')} />
          <WinTab label="Billing" active={view === 'billing'} onClick={() => setView('billing')} />
          <span style={{ flex: 1 }} />
          {/* The schedule is changed on the Schedule tab, not here (Duncan 2026-10-22). */}
          <button type="button" onClick={() => { onClose(); navigate(`/projects/${task.project_slug || task.project_id}/tasks`); }} style={{ ...linkBtn, fontWeight: 500, paddingBottom: 9 }}>
            Open on the Schedule <Icon name="external-link" size={12} />
          </button>
        </div>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        {view === 'overview' && (() => {
          // The task at a glance: the schedule's %, the sub's (its fixtures in), the lines done, its billing, who's on it.
          const fixturesIn = showWork ? lines.reduce((n, l) => { const st = stateOf(l); return n + (st.done ? (l.pro_qty || 0) : Math.min(l.pro_qty || 0, Number(st.installed) || 0)); }, 0) : null;
          const subPct = fixturesIn != null && totals.in > 0 ? Math.round((fixturesIn / totals.in) * 100) : null;
          const links = (subSov && subSov.links) || [];
          const numbered = subSov ? numberLines(subSov.lines || [], subSov.sections || []).lines : [];
          const billed = links.reduce((n, k) => n + k.allocation_amount_cents, 0);
          const first = links[0];
          const firstLine = first && numbered.find((l) => l.id === first.line_id);
          const tile = { padding: '10px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', minWidth: 0 };
          const big = { fontSize: 21, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' };
          const go = (v, text) => <button type="button" onClick={() => setView(v)} style={{ ...linkBtn, fontSize: 12.5 }}>{text}</button>;
          const people = (progress && progress.technicians) || [];
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                <div style={tile}>
                  <div style={muted}>Schedule</div>
                  <div className="mono" style={big}>{task.percent_complete}%</div>
                  <div style={muted}>{fmtSpan(task.start_date, task.finish_date, todayYmd()) || 'No dates yet'}</div>
                </div>
                {isDocs ? (
                  <div style={tile}>
                    <div style={muted}>Documents</div>
                    <div style={{ ...big, fontSize: 15, fontWeight: 600, marginTop: 4 }}>The sub’s and E3’s</div>
                    <div>{go('work', 'Open the Documents tab')}</div>
                  </div>
                ) : lines.length ? (
                  <>
                    <div style={tile}>
                      <div style={muted}>Sub says</div>
                      <div className="mono" style={{ ...big, color: subPct != null && subPct !== task.percent_complete ? 'var(--warning)' : 'var(--text-1)' }}>{subPct != null ? `${subPct}%` : '—'}</div>
                      <div style={muted}>{fixturesIn != null ? `${fixturesIn.toLocaleString()} of ${totals.in.toLocaleString()} fixtures in` : 'E3 Subs isn’t showing the line work'}</div>
                    </div>
                    <div style={tile}>
                      <div style={muted}>Lines complete</div>
                      <div className="mono" style={big}>{pr ? pr.done : '—'}<span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}> of {lines.length}</span></div>
                      <div style={muted}>{pr ? `${pr.issues} open issue${pr.issues === 1 ? '' : 's'} · ${pr.punch} punch item${pr.punch === 1 ? '' : 's'}` : ''} {go('work', 'Work tab')}</div>
                    </div>
                  </>
                ) : (
                  <div style={tile}>
                    <div style={muted}>Work</div>
                    <div style={{ ...big, fontSize: 15, fontWeight: 600, marginTop: 4, color: 'var(--warning)' }}>No work uploaded yet</div>
                    <div>{go('work', 'Upload the workbook on the Work tab')}</div>
                  </div>
                )}
                <div style={tile}>
                  <div style={muted}>Billing</div>
                  <div className="mono" style={{ ...big, color: links.length ? 'var(--text-1)' : 'var(--text-3)' }}>{links.length ? moneyOfCents(billed) : 'Not linked'}</div>
                  <div style={muted}>
                    {first && firstLine ? `line ${firstLine.number} · ${Math.round((first.allocation_amount_cents / Math.max(1, firstLine.value_cents)) * 100)}%${links.length > 1 ? ` + ${links.length - 1} more` : ''} · ` : task.subcontractor ? 'Link it to the sub’s SOV · ' : 'No sub on it'}
                    {task.subcontractor && go('billing', 'Billing tab')}
                  </div>
                </div>
              </div>
              {people.length > 0 && (
                <div style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
                  <span style={muted}>On it: </span>{people.map((x) => x.name).join(', ')}
                </div>
              )}
            </div>
          );
        })()}

        {view === 'billing' && (task.subcontractor ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Sub SOV links</span>
              <span style={muted}>what {task.subcontractor.name} bills E3 for this task, from its schedule of values (the sub’s Billing tab)</span>
            </div>
            <TaskSubSovLinks task={{ id: task.id, subcontractor_id: task.subcontractor.id, responsible_party: task.subcontractor.name }} onChanged={loadSubSov}
              onAddContract={onOpenSub ? leaveFor('subcontract') : undefined} onAddLines={onOpenSub ? leaveFor('billing') : undefined} />
          </div>
        ) : (
          <div style={{ ...muted, fontSize: 13.5 }}>This task has no sub as its Responsible Party, so it has nothing to bill from a sub.</div>
        ))}

        {/* The Work: kept while another tab shows (a line half edited, an upload going). */}
        <div style={{ display: view === 'work' ? 'flex' : 'none', flexDirection: 'column', gap: 12, ...(isDocs ? { flex: 1, minHeight: 0 } : {}) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{isDocs ? 'The documents' : 'The work'}</span>
          <span style={muted}>{isDocs ? 'the sub’s and E3’s, the same list as on E3 Subs' : 'what the sub works, line by line'}</span>
        </div>

        {/* A Documents task's work is its documents, kept on E3 Subs (2026-10-24): no workbook here. */}
        {isDocs ? (
          <TaskDocuments task={task} confirm={confirm} onUploadingChange={onDocsUploading} canSend={canSendToCloseout} />
        ) : (importing || (!lines.length && !file)) ? (
          <div style={{ padding: '14px 16px', borderRadius: 10, border: '1px dashed var(--border-strong)', background: 'var(--bg-surface)' }}>
            {!lines.length && !importing && <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 10 }}>No work on this task yet. Upload the lighting team’s fixture workbook and pick this task’s site.</div>}
            <ImportStep task={task} hasLines={lines.length > 0} busy={busy} onImport={doImport} onCancel={importing ? () => setImporting(false) : null} />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px 14px', flexWrap: 'wrap', fontSize: 13.5 }}>
              <Icon name="template" size={14} style={{ color: 'var(--text-3)' }} />
              <span>{file ? file.filename : 'Lines added in Summit'}{site ? <span style={{ color: 'var(--text-3)' }}> · {site}</span> : null}</span>
              {file && <span style={muted}>{file.uploaded_by_name ? `added by ${file.uploaded_by_name}, ` : ''}{fmtAdded(file.created_at)}</span>}
              {file && <button type="button" onClick={() => downloadTaskWorkFile(file).catch((e) => setError(e.message || 'Download failed'))} style={linkBtn}>Download</button>}
              <button type="button" onClick={async () => { if (await okToDrop()) { leaveLine(); setImporting(true); } }} style={linkBtn}>Replace</button>
              <button type="button" onClick={removeAll} style={{ ...linkBtn, color: 'var(--danger)' }}>Remove</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
              {[['Lines', totals.lines], ['Fixtures out', totals.out], ['Fixtures in', totals.in], ['Rooms and areas', totals.rooms]].map(([label, v]) => (
                <div key={label} style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
                  <div style={muted}>{label}</div>
                  <div className="mono" style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-1)' }}>{v.toLocaleString()}</div>
                </div>
              ))}
            </div>
            {showWork && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                <div style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--success-soft)', border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)' }}>
                  <div style={muted}>Lines complete</div>
                  <div className="mono" style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-1)' }}>{pr.done}<span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}> of {lines.length}</span></div>
                  <div style={{ height: 4, borderRadius: 2, background: 'color-mix(in oklch, var(--success) 18%, transparent)', marginTop: 4 }}>
                    <div style={{ width: `${lines.length ? Math.round((pr.done / lines.length) * 100) : 0}%`, height: '100%', borderRadius: 2, background: 'var(--success)' }} />
                  </div>
                </div>
                {[['Open issues', pr.issues, 'var(--warning)'], ['Spot checks to walk', pr.walk, 'var(--accent-text)'], ['Punch items open', pr.punch, 'var(--danger)']].map(([label, v, tone]) => (
                  <div key={label} style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
                    <div style={muted}>{label}</div>
                    <div className="mono" style={{ fontSize: 19, fontWeight: 700, color: v ? tone : 'var(--text-1)' }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            {progress && progress.on && progress.error && (
              <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', borderRadius: 9, fontSize: 13.5, color: 'var(--warning)', background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)' }}>
                E3 Subs couldn’t be reached, so the line work (complete, photos, issues) isn’t showing.
                <button type="button" onClick={loadProgress} style={linkBtn}>Try again</button>
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <div className="tw-table" style={{ minWidth: 760 }}>
                <div className="tw-head" style={{ display: 'grid', gridTemplateColumns: showWork ? ROW_WORK : ROW, gap: 10, padding: '0 0 4px', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                  <span>Line</span><span>Room / area</span><span>Existing</span><span>Proposed</span><span>Sensor</span>{showWork && <><span>Complete</span><span>Photos</span></>}<span />
                </div>
                {groups.map((g) => {
                  const isOpen = open.has(g.building);
                  return (
                    <section key={g.building} aria-label={g.building} style={{ borderTop: '1px solid var(--border-default)' }}>
                      <button type="button" onClick={() => toggle(g.building)} aria-expanded={isOpen}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 0', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                        <Icon name="caret-r" size={13} style={{ color: 'var(--text-3)', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }} />
                        <span style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)' }}>{g.building}</span>
                        <span style={muted}>{g.lines.length} line{g.lines.length === 1 ? '' : 's'} · {g.fixtures.toLocaleString()} fixture{g.fixtures === 1 ? '' : 's'} in</span>
                      </button>
                      {isOpen && (
                        <div style={{ paddingBottom: 8 }}>
                          {g.lines.map((l) => {
                            if (editing !== l.id) {
                              return (
                                <LineRow key={l.id} line={l} flash={savedId === l.id} showWork={showWork} work={stateOf(l)} onEdit={() => startEdit(l.id)} />
                              );
                            }
                            const formProps = {
                              initial: l, title: l.line_no != null ? `Line ${l.line_no}` : 'Line added in Summit', busy, error: formError,
                              onDirty, onSave: (f) => saveLine(l, f), onCancel: cancelEdit, onRemove: () => removeLine(l),
                            };
                            return showWork ? (
                              <LinePanel key={l.id} line={l} tab={tab} onTab={setTab} work={stateOf(l)} onWorkChange={onWorkChange} onDirtyWork={onDirtyWork}
                                confirm={confirm} onExpired={onExpired} onClose={cancelEdit} formProps={formProps} />
                            ) : (
                              <LineForm key={l.id} {...formProps} />
                            );
                          })}
                          {editing === `new:${g.building}` ? (
                            <LineForm initial={{ building: g.building === '(no building)' ? '' : g.building }} title="New line" busy={busy} error={formError}
                              onDirty={onDirty} onSave={(f) => saveLine(null, f)} onCancel={cancelEdit} />
                          ) : (
                            <button type="button" onClick={() => startEdit(`new:${g.building}`)} style={{ ...linkBtn, marginTop: 6 }}>+ Add a line</button>
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}
                {!groups.length && <div style={{ ...muted, padding: '10px 0' }}>No lines. Replace to import the workbook again, or add one.</div>}
              </div>
            </div>
          </>
        )}
        </div>
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </ModalShell>
  );
}
